import { Box, Columns, FileText, LayoutGrid, Repeat, Trash2, Type } from 'lucide-react';
import type { ColumnNode } from '@news-cms/shared';
import { useEditor } from './editor-context';
import { NodeToolbar } from './node-toolbar';
import { ContainerChildrenRenderer } from './node-renderer';

const SPAN_PRESETS: { label: string; span: number }[] = [
  { label: '1/4', span: 3 },
  { label: '1/3', span: 4 },
  { label: '1/2', span: 6 },
  { label: '2/3', span: 8 },
  { label: '3/4', span: 9 },
  { label: 'full', span: 12 },
];

export function ColumnNodeEditor({ node }: { node: ColumnNode }) {
  const {
    selectedId,
    select,
    updateColumn,
    addContainerToParent,
    addRowToParent,
    addSlotToParent,
    addCarouselToParent,
    addLabelToParent,
    remove,
  } = useEditor();
  const isSelected = selectedId === node.id;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded border px-px pb-0.5 pt-2 transition ${
        isSelected
          ? 'border-purple-400 bg-purple-50/40 dark:border-purple-500 dark:bg-purple-950/20'
          : 'border-slate-200 bg-slate-50/40 dark:border-slate-700 dark:bg-slate-800/30'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
        <Columns className="h-3 w-3" />
        Column · span {node.span}/12
      </div>

      <NodeToolbar
        visible={isSelected}
        extra={
          <div className="mr-1 flex items-center gap-0.5 border-r border-slate-200 pr-2 dark:border-slate-700">
            {SPAN_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateColumn(node.id, { span: p.span });
                }}
                className={`rounded px-1.5 py-0.5 text-[10px] transition ${
                  node.span === p.span
                    ? 'bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {p.label}
              </button>
            ))}
            <input
              type="number"
              min={1}
              max={12}
              value={node.span}
              onChange={(e) => updateColumn(node.id, { span: Number(e.target.value) })}
              onClick={(e) => e.stopPropagation()}
              className="ml-1 h-6 w-10 rounded border border-slate-200 bg-white px-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        }
        buttons={[
          {
            key: 'row',
            label: 'Row',
            icon: <LayoutGrid className="h-3.5 w-3.5" />,
            onClick: () => addRowToParent(node.id),
          },
          {
            key: 'container',
            label: 'Container',
            icon: <Box className="h-3.5 w-3.5" />,
            onClick: () => addContainerToParent(node.id),
          },
          {
            key: 'slot',
            label: 'Slot',
            icon: <FileText className="h-3.5 w-3.5" />,
            onClick: () => addSlotToParent(node.id),
          },
          {
            key: 'carousel',
            label: 'Carousel',
            icon: <Repeat className="h-3.5 w-3.5" />,
            onClick: () => addCarouselToParent(node.id),
          },
          {
            key: 'label',
            label: 'Label',
            icon: <Type className="h-3.5 w-3.5" />,
            onClick: () => addLabelToParent(node.id),
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
          },
        ]}
      />

      <ContainerChildrenRenderer children={node.children} />
    </div>
  );
}
